/**
 * CredentialEntryCard — generic credential-save HITL renderer (story 23922).
 *
 * The substrate emits a ``credential_save``-typed HITL question when a
 * workflow declares ``required_credentials`` and the user has no saved
 * credential matching the declared name. This card is the form host: it
 * renders the credential's display name as the title, an optional
 * human-readable description, and a password-masked input — wired via
 * ``HitlSubmitter`` + ``JsonSchemaForm`` so the existing ``sensitivity.ts``
 * pattern handles masking.
 *
 * Shared between ora (main UI) and the Outlook addin: ``HitlEventDispatcher``
 * in both surfaces dispatches on ``hitlQuestion.type === "credential_save"``
 * and mounts this card. Submit calls the same ``resumeInstance`` /
 * ``useFetcher`` transport as every other HITL — no new product API.
 *
 * The card is intentionally generic against the credential name. Workflows
 * declare what they need via ``PhaseSpec.required_credentials``; the
 * substrate's ``credential_save_question`` helper renders the wire payload
 * with ``data.display_name`` (already title-cased) and an optional
 * ``data.description``. No Epic-specific or carrier-specific copy lives here.
 */

import { useCallback } from "react";
import { HitlSubmitter } from "./HitlSubmitter";

export interface CredentialEntryCardProps {
  /** The credential_save HITL question payload (forwarded from the substrate). */
  hitlQuestion: Record<string, unknown> | null | undefined;
  /** Called with the unwrapped form values on submit; parent wires the transport. */
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  /** Optional cancel affordance — leaves the workflow paused. */
  onCancel?: () => void;
  /** Submit label override (defaults to "Save credentials"). */
  submitLabel?: string;
  /** Externally controlled submitting state for parents managing async state. */
  submittingOverride?: boolean;
}

export default function CredentialEntryCard({
  hitlQuestion,
  onSubmit,
  onCancel,
  submitLabel = "Save credentials",
  submittingOverride,
}: CredentialEntryCardProps) {
  const data = (hitlQuestion as Record<string, unknown>)?.data as
    | Record<string, unknown>
    | undefined;
  const displayName = (data?.display_name as string | undefined) ?? "Credentials";
  const description = data?.description as string | undefined;

  const submitWrapper = useCallback(
    async (values: Record<string, unknown>) => {
      await onSubmit(values);
    },
    [onSubmit],
  );

  return (
    <div className="flex flex-col gap-3" data-testid="credential-entry-card">
      <h2 className="text-lg font-bold text-primary-gray-light-900">
        Save your {displayName}
      </h2>
      {description ? (
        <p className="text-sm text-primary-gray-light-600">{description}</p>
      ) : null}
      <HitlSubmitter
        hitlQuestion={hitlQuestion}
        onSubmit={submitWrapper}
        onCancel={onCancel}
        submitLabel={submitLabel}
        submittingOverride={submittingOverride}
      />
    </div>
  );
}
